import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { IPagination } from '@/types/general.type';
import { TAgentInvitation } from '../types/agent.type';
import { TFetchParams } from '@/types/fetch.type';

/**
 *
 */
export type TGetListInviteAgentResponse = ApiResponse<IPagination<TAgentInvitation>>;
export type TGetListInviteAgentParams = TFetchParams;

export const getListInviteAgent = async (params?: TGetListInviteAgentParams): Promise<TGetListInviteAgentResponse> => {
  const url = '/invitation';

  return axiosInstance.get(url, { params });
};

/**
 *  Return query key for get invitation
 */
export const GET_LIST_INVITE_AGENT_QUERY_KEY = 'get-list-invite-agent';

export const getListInviteAgentQueryOptions = <TData = IPagination<TAgentInvitation>>(
  params: TGetListInviteAgentParams,
  select?: (data: TGetListInviteAgentResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_INVITE_AGENT_QUERY_KEY,
      {
        search: params?.search || '',
        page: params?.page || 1,
        limit: params?.limit || 10,
        sortBy: params?.sortBy || 'fullname',
        sortDir: params?.sortDir || 'ASC',
      },
    ],
    queryFn: () => getListInviteAgent(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListInviteAgentQuery<TData>(options: {
  params: TGetListInviteAgentParams;
  queryConfig?: QueryConfig<typeof getListInviteAgentQueryOptions<TData>>;
  select: (data: TGetListInviteAgentResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListInviteAgentQuery(options: {
  params: TGetListInviteAgentParams;
  queryConfig?: QueryConfig<typeof getListInviteAgentQueryOptions<TGetListInviteAgentResponse>>;
}): ReturnType<typeof useQuery<TGetListInviteAgentResponse>>;

// Implementation
export function useGetListInviteAgentQuery<TData = TGetListInviteAgentResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListInviteAgentParams;
  queryConfig?: QueryConfig<typeof getListInviteAgentQueryOptions<TData | TGetListInviteAgentResponse>>;
  select?: (data: TGetListInviteAgentResponse) => TData;
}) {
  return useQuery({
    ...getListInviteAgentQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListInviteAgentResponse ? TGetListInviteAgentResponse : TData>>;
}
